import { faCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { CheckIconWrapper } from './CopyButton.styled';
import { ICopyButtonProps } from './types';

export const CopyButton: FC<ICopyButtonProps> = ({
  textToCopy,
  onCopy,
  checkmarkDuration = 1500,
  disabled = false,
  forceState = null
}) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const resetCopiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetCopiedTimerRef.current !== null) {
        clearTimeout(resetCopiedTimerRef.current);
        resetCopiedTimerRef.current = null;
      }
    };
  }, []);

  return useMemo(() => {
    return (isCopied || forceState === 'checkmark') && forceState !== 'copy' ? (
      <CheckIconWrapper>
        <Icon
          icon={faCheck}
          disabled={true}
          title={t('common.copy', { defaultValue: 'Copy' })}
          data-testid={'checkmark'}
        />
      </CheckIconWrapper>
    ) : (
      <Icon
        icon={faCopy}
        disabled={disabled}
        title={t('common.copy', { defaultValue: 'Copy' })}
        data-testid={'copy'}
        onClick={async (e) => {
          e.stopPropagation();
          if (isCopied || disabled) {
            return;
          }

          await navigator.clipboard.writeText(textToCopy);
          onCopy?.();

          setIsCopied(true);

          if (resetCopiedTimerRef.current !== null) {
            clearTimeout(resetCopiedTimerRef.current);
          }
          resetCopiedTimerRef.current = setTimeout(() => {
            resetCopiedTimerRef.current = null;
            setIsCopied(false);
          }, checkmarkDuration);
        }}
      />
    );
  }, [isCopied, forceState, disabled, textToCopy, onCopy, checkmarkDuration, t]);
};

export default CopyButton;
